import { IconProp } from "@fortawesome/fontawesome-svg-core"
import clsx from "clsx"
import { Toaster as SonnerToaster, toast as sonnerToast } from "sonner"

import useIsDesktop from "@hooks/useIsDesktop"

import { faIcon } from "@utils/faIcon"

import { ExtraTouchArea } from "@particles/index"

import { Icon, IconButton, LinkButton } from "@atoms"

import styles from "./Toast.module.scss"
import { ToastProps } from "./Toast.types"

export function ToastContent(props: ToastProps & { onClose?: () => void }) {
    const { type, variant = "default", title, supportText, cta, secondaryCta, iconOverride, onClose } = props

    const iconMap: { [key in ToastProps["type"]]: IconProp } = {
        normal: faIcon({ name: "circle-question", style: "solid" }),
        info: faIcon({ name: "circle-info", style: "solid" }),
        success: faIcon({ name: "circle-check", style: "solid" }),
        warning: faIcon({ name: "triangle-exclamation", style: "solid" }),
        error: faIcon({ name: "circle-exclamation", style: "solid" }),
        loading: faIcon({ name: "spinner-third", style: "solid" }),
    }
    const toastIcon = iconMap[type] || faIcon({ name: "circle", style: "solid" })

    return (
        <div className={clsx(styles.base, styles[variant], styles[`${type}Type`])}>
            <Icon
                size={14}
                icon={iconOverride || toastIcon}
                containerClassName={styles.iconContainer}
                className={styles.icon}
                spin={type === "loading"}
            />
            <div className={styles.copyAndCTAs}>
                <div className={styles.titleAndSupportText}>
                    <span className={styles.title} data-title={title}>
                        {title}
                    </span>
                    {variant === "detailed" && supportText && (
                        <div data-description={supportText} className={styles.supportText}>
                            {supportText}
                        </div>
                    )}
                </div>

                {variant === "detailed" && (cta || secondaryCta) && (
                    <div className={styles.ctaContainer}>
                        {cta && (
                            <ExtraTouchArea extraTouchArea={8}>
                                <LinkButton {...cta} colorScheme="black" />
                            </ExtraTouchArea>
                        )}
                        {secondaryCta && (
                            <ExtraTouchArea extraTouchArea={8}>
                                <LinkButton {...secondaryCta} colorScheme="black" />
                            </ExtraTouchArea>
                        )}
                    </div>
                )}
            </div>

            {variant === "default" && cta && (
                <ExtraTouchArea extraTouchArea={8}>
                    <LinkButton {...cta} colorScheme="black" className={styles.defaultCta} />
                </ExtraTouchArea>
            )}
            <ExtraTouchArea extraTouchArea={7}>
                <IconButton
                    size="xs"
                    variant="muted"
                    icon={faIcon({ name: "xmark", style: "regular" })}
                    onClick={onClose}
                    noDebounce={true}
                />
            </ExtraTouchArea>
        </div>
    )
}

export default function toast(props: ToastProps) {
    return sonnerToast.custom((t) => <ToastContent {...props} onClose={() => sonnerToast.dismiss(t)} />)
}

export function Toaster() {
    const isDesktop = useIsDesktop()

    return (
        <SonnerToaster
            closeButton={true}
            className={styles.toaster}
            offset={isDesktop ? "1.5rem" : "0.5rem"}
            position={isDesktop ? "bottom-right" : "top-center"}
            toastOptions={{
                classNames: {
                    toast: styles.toastBase,
                },
            }}
        />
    )
}
